import { dayKey, partsInZone, parseUtc } from './dateTime';

// Column types and typed filter conditions for the STUI Column Filter (components/common/columnfilter).
//
// A column is 'text', 'number', 'date' or 'none' (sort only). The type is INFERRED from the data
// (P5-1): field metadata's uI_Type describes the input control, not the data, so it cannot say
// "number". A column's `filterType` overrides the inference; `filterable: false` means 'none' and
// `filterable: 'text'` means text conditions only.
//
// A condition filter is { type, join: 'and'|'or', conds: [{ op, v, v2? }] } with one or two
// conditions — Excel's Custom AutoFilter. Relative date operators (today, thisMonth, ...) stay
// relative in state and URLs and are resolved against the local calendar when applied; server-backed
// grids resolve them to explicit dates before sending (resolveRelativeDates), so both agree.

export const FILTER_TYPES = ['text', 'number', 'date'];

// Operator catalogs, in menu order. `sep` marks a divider before the item (Excel's grouping).
export const FILTER_OPS = {
  text: [
    { value: 'equals', label: 'Equals' },
    { value: 'notEquals', label: 'Does not equal' },
    { value: 'beginsWith', label: 'Begins with', sep: true },
    { value: 'endsWith', label: 'Ends with' },
    { value: 'contains', label: 'Contains', sep: true },
    { value: 'notContains', label: 'Does not contain' },
  ],
  number: [
    { value: 'eq', label: 'Equals' },
    { value: 'ne', label: 'Does not equal' },
    { value: 'gt', label: 'Greater than', sep: true },
    { value: 'ge', label: 'Greater than or equal to' },
    { value: 'lt', label: 'Less than' },
    { value: 'le', label: 'Less than or equal to' },
    { value: 'between', label: 'Between', sep: true },
  ],
  date: [
    { value: 'equals', label: 'Equals' },
    { value: 'before', label: 'Before' },
    { value: 'after', label: 'After' },
    { value: 'between', label: 'Between' },
    { value: 'today', label: 'Today', sep: true },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'thisWeek', label: 'This week', sep: true },
    { value: 'lastWeek', label: 'Last week' },
    { value: 'thisMonth', label: 'This month', sep: true },
    { value: 'lastMonth', label: 'Last month' },
    { value: 'thisYear', label: 'This year', sep: true },
    { value: 'lastYear', label: 'Last year' },
  ],
};

export const RELATIVE_DATE_OPS = new Set(['today', 'yesterday', 'thisWeek', 'lastWeek', 'thisMonth', 'lastMonth', 'thisYear', 'lastYear']);
const opsFor = (type) => new Set((FILTER_OPS[type] || []).map(o => o.value));

export const SORT_LABELS = {
  text:   { asc: 'Sort A to Z', desc: 'Sort Z to A' },
  number: { asc: 'Sort Smallest to Largest', desc: 'Sort Largest to Smallest' },
  date:   { asc: 'Sort Oldest to Newest', desc: 'Sort Newest to Oldest' },
  none:   { asc: 'Sort A to Z', desc: 'Sort Z to A' },
};
export const TYPE_MENU_LABEL = { text: 'Text Filters', number: 'Number Filters', date: 'Date Filters' };

// ── Dates ────────────────────────────────────────────────────────────────────────────────────
// Everything compares at DAY granularity as a day number (days since 1970-01-01, calendar based).
// A "day" is the person's local day (utils/dateTime): a timestamp ('2026-08-18T14:03:00Z', or
// zoneless, which is UTC) lands on the day it shows on in their zone, so a 9 PM Central entry is
// filed under that evening, not under tomorrow's UTC date. A plain date ('2026-08-18', a SQL date
// column) is its own literal day. Also accepts US ('08/18/2026', what a formatted cell shows) and
// 'Aug 18, 2026'.
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})/;
const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
const US_DAY = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/;
const MONTH_DAY_YEAR = /^(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\.?\s+\d{1,2},?\s+\d{4}\b/i;
const dayNumber = (y, m, d) => Math.round(Date.UTC(y, m - 1, d) / 86400000);

export const parseDay = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) {
    if (isNaN(value)) return null;
    const p = partsInZone(value);
    return dayNumber(p.year, p.month, p.day);
  }
  const s = String(value).trim();
  if (ISO_TIMESTAMP.test(s)) {
    const key = dayKey(s, { valueZone: 'utc' });
    if (key) return dayNumber(+key.slice(0, 4), +key.slice(5, 7), +key.slice(8, 10));
  }
  let m = ISO_DAY.exec(s);
  if (m) return dayNumber(+m[1], +m[2], +m[3]);
  m = US_DAY.exec(s);
  if (m) return dayNumber(+m[3], +m[1], +m[2]);
  // Only a real "Month day, year" form reaches Date.parse — V8's parser is lenient and would call
  // text like "Pump 10" a date, turning a text column into a date column.
  if (!MONTH_DAY_YEAR.test(s)) return null;
  const t = Date.parse(s);
  if (isNaN(t)) return null;
  const d = new Date(t);
  return dayNumber(d.getFullYear(), d.getMonth() + 1, d.getDate());
};

/**
 * A sortable number for a date cell: a timestamp sorts by its instant (so two entries on one day
 * keep their order), a plain date by its day. Blank / unparseable = -Infinity (sorts first).
 */
export const dateSortValue = (value) => {
  if (value instanceof Date) return isNaN(value) ? -Infinity : value.getTime();
  const s = value === null || value === undefined ? '' : String(value).trim();
  if (ISO_TIMESTAMP.test(s)) {
    const d = parseUtc(s);
    if (d) return d.getTime();
  }
  const day = parseDay(s);
  return day === null ? -Infinity : day * 86400000;
};

/** Day number -> { y, m, d } (calendar). */
export const dayParts = (day) => {
  const dt = new Date(day * 86400000);
  return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
};

/** Day number -> 'YYYY-MM-DD'. */
export const dayToIso = (day) => {
  const { y, m, d } = dayParts(day);
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
};

/** [startDay, endDayExclusive) for a relative operator, against `now` (the person's local calendar, weeks start Sunday). */
export const relativeRange = (op, now = new Date()) => {
  const p = partsInZone(now);
  const today = dayNumber(p.year, p.month, p.day);
  const y = p.year;
  const m = p.month;
  const weekStart = today - new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay();
  switch (op) {
    case 'today':     return [today, today + 1];
    case 'yesterday': return [today - 1, today];
    case 'thisWeek':  return [weekStart, weekStart + 7];
    case 'lastWeek':  return [weekStart - 7, weekStart];
    case 'thisMonth': return [dayNumber(y, m, 1), m === 12 ? dayNumber(y + 1, 1, 1) : dayNumber(y, m + 1, 1)];
    case 'lastMonth': return [m === 1 ? dayNumber(y - 1, 12, 1) : dayNumber(y, m - 1, 1), dayNumber(y, m, 1)];
    case 'thisYear':  return [dayNumber(y, 1, 1), dayNumber(y + 1, 1, 1)];
    case 'lastYear':  return [dayNumber(y - 1, 1, 1), dayNumber(y, 1, 1)];
    default:          return null;
  }
};

// ── Inference ────────────────────────────────────────────────────────────────────────────────
const INFER_SAMPLE = 200;

/** 'number' when every non-empty value is a real number, 'date' when every one parses as a day, else 'text'. */
export const inferFilterType = (values) => {
  let seen = 0;
  let numbers = true;
  let dates = true;
  for (const v of values || []) {
    if (v === null || v === undefined || v === '') continue;
    seen++;
    if (typeof v !== 'number') numbers = false;
    if (typeof v === 'number' || typeof v === 'boolean' || parseDay(v) === null) dates = false;
    if ((!numbers && !dates) || seen >= INFER_SAMPLE) break;
  }
  if (!seen) return 'text';
  if (numbers) return 'number';
  if (dates) return 'date';
  return 'text';
};

/** The effective type of a grid column, given a sample of its values. */
export const columnFilterType = (column, values) => {
  if (!column || column.filterable === false) return 'none';
  if (column.filterType) return column.filterType;
  if (column.filterable === 'text') return 'text';
  return inferFilterType(values);
};

// ── Conditions ───────────────────────────────────────────────────────────────────────────────
const blank = (x) => x === undefined || x === null || String(x).trim() === '';

/** Is this one condition complete for its type? */
export const isCompleteCondition = (cond, type) => {
  if (!cond || !opsFor(type).has(cond.op)) return false;
  if (type === 'date' && RELATIVE_DATE_OPS.has(cond.op)) return true;
  if (cond.op === 'between') return !blank(cond.v) && !blank(cond.v2);
  if (type === 'number') return !blank(cond.v) && !isNaN(Number(cond.v));
  if (type === 'date') return parseDay(cond.v) !== null;
  return !blank(cond.v);
};

/**
 * Normalize anything filter-shaped into a condition filter, or null when it is not one.
 * Accepts Phase 4's { op, text } (one text condition) so older URLs and memory keep working.
 */
export const normalizeConditionFilter = (f) => {
  if (!f || Array.isArray(f) || typeof f !== 'object') return null;
  if (!f.conds && typeof f.op === 'string') {
    return normalizeConditionFilter({ type: 'text', join: 'and', conds: [{ op: f.op, v: f.text }] });
  }
  const type = FILTER_TYPES.includes(f.type) ? f.type : null;
  if (!type || !Array.isArray(f.conds)) return null;
  const conds = f.conds
    .filter(c => isCompleteCondition(c, type))
    .slice(0, 2)
    .map(c => {
      const out = { op: c.op };
      if (!(type === 'date' && RELATIVE_DATE_OPS.has(c.op))) out.v = String(c.v).trim();
      if (c.op === 'between') out.v2 = String(c.v2).trim();
      return out;
    });
  if (!conds.length) return null;
  return { type, join: f.join === 'or' ? 'or' : 'and', conds };
};

export const isConditionFilter = (f) => normalizeConditionFilter(f) !== null;

const matchText = (value, { op, v }) => {
  const s = String(value || '').toLowerCase();
  const t = String(v).trim().toLowerCase();
  switch (op) {
    case 'equals':      return s === t;
    case 'notEquals':   return s !== t;
    case 'beginsWith':  return s.startsWith(t);
    case 'endsWith':    return s.endsWith(t);
    case 'contains':    return s.includes(t);
    case 'notContains': return !s.includes(t);
    default:            return true;
  }
};

const matchNumber = (value, { op, v, v2 }) => {
  const isBlank = value === null || value === undefined || value === '';
  const n = isBlank ? NaN : Number(value);
  if (isNaN(n)) return op === 'ne'; // blanks and non-numbers only pass "does not equal"
  const a = Number(v);
  switch (op) {
    case 'eq': return n === a;
    case 'ne': return n !== a;
    case 'gt': return n > a;
    case 'ge': return n >= a;
    case 'lt': return n < a;
    case 'le': return n <= a;
    case 'between': { const b = Number(v2); return n >= Math.min(a, b) && n <= Math.max(a, b); }
    default: return true;
  }
};

const matchDate = (value, { op, v, v2 }, now) => {
  const d = parseDay(value);
  if (d === null) return false;
  if (RELATIVE_DATE_OPS.has(op)) {
    const [start, end] = relativeRange(op, now);
    return d >= start && d < end;
  }
  const a = parseDay(v);
  switch (op) {
    case 'equals':  return d === a;
    case 'before':  return d < a;
    case 'after':   return d > a;
    case 'between': { const b = parseDay(v2); return d >= Math.min(a, b) && d <= Math.max(a, b); }
    default: return true;
  }
};

/** One cell against a normalized condition filter. */
export const matchConditionFilter = (value, filter, now = new Date()) => {
  const f = normalizeConditionFilter(filter);
  if (!f) return true;
  const test = (c) => (f.type === 'number' ? matchNumber(value, c)
    : f.type === 'date' ? matchDate(value, c, now)
    : matchText(value, c));
  return f.join === 'or' ? f.conds.some(test) : f.conds.every(test);
};

/**
 * For server-backed grids: relative date conditions become explicit 'between' days (the client's
 * local calendar), so the server never has to guess the user's "today".
 */
export const resolveRelativeDates = (filters, now = new Date()) => {
  const out = {};
  for (const [key, f] of Object.entries(filters || {})) {
    const cf = normalizeConditionFilter(f);
    if (!cf || cf.type !== 'date') { out[key] = f; continue; }
    out[key] = {
      ...cf,
      conds: cf.conds.map(c => {
        if (!RELATIVE_DATE_OPS.has(c.op)) return c;
        const [start, end] = relativeRange(c.op, now);
        return { op: 'between', v: dayToIso(start), v2: dayToIso(end - 1) };
      }),
    };
  }
  return out;
};

/** Human summary of a condition filter, e.g. 'Begins with "PMP" or Ends with "A"'. */
export const describeConditionFilter = (filter) => {
  const f = normalizeConditionFilter(filter);
  if (!f) return '';
  const label = (op) => (FILTER_OPS[f.type].find(o => o.value === op) || {}).label || op;
  const one = (c) => {
    if (RELATIVE_DATE_OPS.has(c.op)) return label(c.op);
    if (c.op === 'between') return `${label(c.op)} ${c.v} and ${c.v2}`;
    return `${label(c.op)} "${c.v}"`;
  };
  return f.conds.map(one).join(f.join === 'or' ? ' or ' : ' and ');
};
